import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";

export type PaymentScheduleListParams = {
	saleId?: string;
	isPaid?: boolean;
	dateFrom?: string;
	dateTo?: string;
	page?: number;
	limit?: number;
};

export type BackendPaymentSchedule = {
	id: string;
	saleId: string;
	paymentId: string | null;
	dueDate: string;
	amount: number;
	isPaid: boolean;
	paidAt: string | null;
	paidAmount: number;
	note: string | null;
	createdAt: string;
	updatedAt: string;
	saleNumber: number;
	client: {
		id: string;
		fullName: string;
		phone: string;
	};
	roomNumber: string;
	buildingName: string;
	daysOverdue: number;
	status: "overdue" | "upcoming" | "planned" | "paid" | "partial";
};

export type PaymentScheduleListMeta = {
	total: number;
	page: number;
	limit: number;
	totalPages: number;
	totalOverdueAmount: number;
	totalUpcomingAmount: number;
};

type PaymentScheduleListResponse = {
	success: true;
	data: {
		items: BackendPaymentSchedule[];
		meta: PaymentScheduleListMeta;
	};
};

export type PaymentSchedulesResult = {
	data: BackendPaymentSchedule[];
	meta: PaymentScheduleListMeta;
};

export async function getPaymentSchedules(
	params?: PaymentScheduleListParams
): Promise<PaymentSchedulesResult> {
	const requestParams: Record<string, string | undefined> = {};

	if (params) {
		if (params.saleId !== undefined) {
			requestParams.saleId = params.saleId;
		}
		if (params.isPaid !== undefined) {
			requestParams.isPaid = String(params.isPaid);
		}
		if (params.dateFrom !== undefined) {
			requestParams.dateFrom = params.dateFrom;
		}
		if (params.dateTo !== undefined) {
			requestParams.dateTo = params.dateTo;
		}
		if (params.page !== undefined) {
			requestParams.page = String(params.page);
		}
		if (params.limit !== undefined) {
			requestParams.limit = String(params.limit);
		}
	}

	const response = await apiClient
		.get<PaymentScheduleListResponse>(endpoints.payments.schedules, {
			params: requestParams,
		})
		.catch((error) => {
			console.error("[PaymentSchedulesApi] Failed to fetch payment schedules:", error);
			throw error;
		});

	return { data: response.data.data.items, meta: response.data.data.meta };
}

export async function getAllPaymentSchedules(
	params?: Omit<PaymentScheduleListParams, "limit" | "page">
) {
	const limit = 100;
	let page = 1;
	let combined: BackendPaymentSchedule[] = [];
	let meta: PaymentScheduleListMeta | null = null;

	while (true) {
		const response = await getPaymentSchedules({
			...params,
			limit,
			page,
		});

		combined = [...combined, ...response.data];
		meta = response.meta;

		if (page >= response.meta.totalPages || response.meta.totalPages === 0) {
			break;
		}

		page += 1;
	}

	return {
		data: combined,
		meta:
			meta ??
			({
				total: 0,
				page: 1,
				limit,
				totalPages: 0,
				totalOverdueAmount: 0,
				totalUpcomingAmount: 0,
			} satisfies PaymentScheduleListMeta),
	};
}

export function getPaymentSchedulesErrorMessage(error: unknown): string {
	if (error && typeof error === "object" && "response" in error) {
		const response = (error as any).response;
		if (response?.data?.error?.message) {
			return response.data.error.message;
		}
	}
	return "To'lov grafiklarini yuklashda xatolik yuz berdi";
}
